import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import ModalBody from 'react-bootstrap/ModalBody';
import ModalFooter from 'react-bootstrap/ModalFooter';
import ModalHeader from 'react-bootstrap/ModalHeader';
import classnames from 'classnames';

import Button from '@components/Button';

import { isTablet } from '@utils/AppUtil';

import './style.scss';

interface IModalBaseProps {
  show: boolean;
  onClose: (e?: React.SyntheticEvent<any>) => void;
  closeOnEscape?: boolean;
  closeOnOverlayClick?: boolean;
  titleElement?: any;
  bodyElement?: any;
  footerElement?: any;
  className?: string;
  errorMessage?: string;
  contentOverlay?: boolean;
  enforceFocus?: boolean;
  closeButton?: boolean;
  backdropClassName?: string;
  contentClasses?: string;
  disableSSR?: boolean;
  showFooter?: boolean;
  showHeaderInBody?: boolean;
  showFooterInBody?: boolean;
  showHeader?: boolean;
  diaglogClassName?: string;
  ariaLabel?: string;
}

interface IModalBaseState {
  isVirtualKeyboardOpen: boolean;
  hasMounted: boolean;
}

export default class ModalBase extends React.Component<IModalBaseProps, IModalBaseState> {
  public static defaultProps: IModalBaseProps = {
    show: false,
    onClose: null,
    closeOnEscape: false,
    closeOnOverlayClick: false,
    titleElement: 'REPLACE TITLE',
    bodyElement: 'REPLACE BODY',
    footerElement: 'REPLACE FOOTER',
    contentOverlay: false,
    enforceFocus: true,
    closeButton: false,
    showFooter: true,
    showFooterInBody: false,
    showHeader: true,
    showHeaderInBody: false,
    diaglogClassName: '',
  };

  constructor(props: IModalBaseProps) {
    super(props);

    this.state = {
      isVirtualKeyboardOpen: false,
      hasMounted: false,
    };
  }

  componentDidMount() {
    this.setState({ hasMounted: true });
  }

  componentDidUpdate(prevProps: Readonly<IModalBaseProps>) {
    if (isTablet() && this.props.show !== prevProps.show) {
      const hasOpen = this.props.show;

      const modals = document.querySelectorAll('.modal-open > div[role=dialog] .modal-base.in');

      if (hasOpen) {
        // Reset overflow of opened modals
        modals.forEach((modal: any) => {
          modal.style.overflow = '';
        });
      } else {
        modals.forEach((modal: any, index) => {
          // To set the overflow of focused modal
          if (index === modals.length - 2) {
            modal.style.overflow = 'auto';
          }

          // To set the overflow of unfocused modals
          if (index < modals.length - 2) {
            modal.style.overflow = 'hidden';
          }
        });
      }
    }
  }

  getBody = () => [
    <div
      key={1}
      className={classnames('modal-base__error', {
        'modal-base__error--show': !!this.props.errorMessage,
      })}
    >
      {this.props.errorMessage}
    </div>,
    <div key={2} className={classnames('modal-base__body')}>
      {!this.props.showFooterInBody && !this.props.showHeaderInBody && this.props.bodyElement}
      {this.props.showHeaderInBody && (
        <div key={3} className={classnames('body__cross-icon')} onClick={this.props.onClose}>
          {this.props.titleElement}
        </div>
      )}
      {(this.props.showHeaderInBody || this.props.showFooterInBody) && (
        <div className={classnames('modal__main-body-inside-body')}>
          <div>
            {this.props.showHeaderInBody && (
              <img
                src="https://cdn-icons-png.flaticon.com/512/179/179386.png"
                width="100px"
                height="100px"
                alt="image"
              />
            )}
          </div>
          <div className={classnames('modal-warning-text')}>
            {this.props.showFooterInBody && this.props.showHeaderInBody && <b>{this.props.bodyElement}</b>}
          </div>
          <div>
            {this.props.showFooterInBody && (
              <Button
                content={this.props.footerElement}
                onClick={this.props.onClose}
                bsStyle={ButtonStyle.DANGER}
                className={classnames('modal-body__button')}
              />
            )}
          </div>
        </div>
      )}
    </div>,
  ];

  render() {
    // Note: This check is necessary for the component to work when SSR is not intended (e.g: in case of React Portals)
    if (this.props.disableSSR && !this.state.hasMounted) {
      return null;
    }

    const derivedAriaLabel =
      this.props.ariaLabel ||
      ((typeof this.props.titleElement === 'string' || typeof this.props.titleElement === 'number') &&
        String(this.props.titleElement).trim());
    const ariaLabel = derivedAriaLabel || undefined;

    return (
      <Modal
        aria-label={ariaLabel}
        contentClassName={this.props.contentClasses}
        show={this.props.show}
        onHide={this.props.onClose}
        backdrop={this.props.closeOnOverlayClick ? true : 'static'}
        backdropClassName={this.props.backdropClassName}
        keyboard={this.props.closeOnEscape}
        className={classnames(this.props.className, 'modal-base')}
        enforceFocus={this.props.enforceFocus}
        dialogClassName={this.props.diaglogClassName}
      >
        {this.props.contentOverlay && <div className={classnames('content-overlay')} />}
        {this.props.showHeader && (
          <ModalHeader closeButton={this.props.closeButton}>{this.props.titleElement}</ModalHeader>
        )}
        <ModalBody>{this.getBody()}</ModalBody>
        {this.props.showFooter && <ModalFooter>{this.props.footerElement}</ModalFooter>}
      </Modal>
    );
  }
}
